import { ConciergeBell } from "lucide-react";
import EvStationOutlined from "@mui/icons-material/EvStationOutlined";
import LocalParkingRounded from "@mui/icons-material/LocalParkingRounded";
import { BookingsData } from "../../../utils/apis/bookings_repository";

type BookingKind = Pick<BookingsData, "bookingType" | "parkingSpaceType" | "multiID" | "tenant">;

export type BookingDescription = {
    space: "EV" | "Standard" | "Valet";
    days: "Single day" | "Multi-day";
};

// What kind of booking this is, as the booking app shows it. A series made through the booking service
// stores each day as a single booking with a series ID, so the series ID decides "Multi-day".
export const describeBooking = (booking: BookingKind): BookingDescription => ({
    space: booking.bookingType === "Guest" || booking.tenant === "VALET" ? "Valet" : booking.parkingSpaceType === "ev" ? "EV" : "Standard",
    days: booking.bookingType === "Multi" || Boolean(booking.multiID) ? "Multi-day" : "Single day",
});

// The booking app's ParkingTypeBadge: EV in green, Standard on the muted surface.
export function ParkingTypeBadge({ space }: { space: BookingDescription["space"] }) {
    if (space === "Valet") {
        return (
            <span aria-label="Valet parking" className="inline-flex items-center gap-1.5 rounded-2xl bg-gray-100 px-2.5 py-1.5 text-xs font-bold text-gray-700">
                <ConciergeBell aria-hidden className="h-4 w-4" />
                Valet
            </span>
        );
    }
    const ev = space === "EV";
    const Icon = ev ? EvStationOutlined : LocalParkingRounded;
    return (
        <span aria-label={ev ? "EV parking" : "Standard parking"}
            className={`inline-flex items-center gap-1.5 rounded-2xl px-2.5 py-1.5 text-xs font-bold ${ev ? "bg-ev-container text-ev" : "bg-app-muted text-app-text"}`}>
            <Icon aria-hidden sx={{ fontSize: 16 }} />
            {ev ? "EV" : "Standard"}
        </span>
    );
}

interface BookingTypeChipProps {
    booking: BookingKind;
}

// The parking type badge with the booking's length beside it, as on the app's booking cards.
export default function BookingTypeChip({ booking }: BookingTypeChipProps) {
    const { space, days } = describeBooking(booking);
    return (
        <div className="flex flex-wrap items-center justify-center gap-2">
            <ParkingTypeBadge space={space} />
            {space !== "Valet" && (
                <span className="rounded-lg bg-app-muted px-2.5 py-1.5 text-xs font-semibold text-app-text">{days}</span>
            )}
        </div>
    );
}
